import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, IconButton, FormControl,
    InputLabel, Select, MenuItem, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PayrollService } from '../../services/payroll.service';
import {
    IEmployeeSalary, ISalaryComponent, IEmployeeSalaryCreate, IWageGrade, CLEAR_WAGE_GRADE_SENTINEL,
    WAGE_MODE_UNSPECIFIED,
} from '../../interfaces/payroll.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { EmployeeAutocomplete } from '../common/EmployeeAutocomplete';
import { Segment } from '../../interfaces/accounting.interface';
import { SegmentService } from '../../services/segment.service';
import { WageGradeService } from '../../services/wageGrade.service';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';
import { toLocalIsoDate } from '../../utils/dateUtils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    salary: IEmployeeSalary | null;
    components: ISalaryComponent[];
}

interface WageGradeOption {
    id: string;
    label: string;
}

interface UserOption {
    id: string;
    username: string;
    fullName?: string;
}

const EmployeeSalaryForm: React.FC<Props> = ({ open, onClose, onSaved, salary, components }) => {
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
    const [effectiveFrom, setEffectiveFrom] = useState('');
    const [details, setDetails] = useState<{ salaryComponentId: string; amount: number }[]>([]);
    const [segmentId, setSegmentId] = useState<string | undefined>(undefined);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [wageGrades, setWageGrades] = useState<IWageGrade[]>([]);
    // H3/R7: the currently-assigned grade even when it is not in the active list, so an unrelated
    // edit never silently drops it (the Select below always includes it, labeled "(inactive)").
    const [assignedInactiveGrade, setAssignedInactiveGrade] = useState<
        { id: string; displayName: string } | null>(null);
    // R8/M9: tri-state -- CLEAR_WAGE_GRADE_SENTINEL is the explicit "No grade" choice, a real
    // grade id is an assignment, and this is never left as "untouched" once an employee is picked
    // (see the carry-forward effect below), so what is on screen is always what gets submitted.
    const [wageGradeId, setWageGradeId] = useState<string>(CLEAR_WAGE_GRADE_SENTINEL);
    // M-1 (fix round 1): when the create-mode carry-forward prefill fetch fails for a reason OTHER
    // than "no prior salary exists" (a 404), we cannot know what to carry forward -- submitting
    // the clear sentinel in that case would destroy a real grade assignment via a save that never
    // mentioned it (the same H3/H6 failure mode). When true, wageGradeId is omitted from the
    // submit payload entirely so the backend's own R8 carry-forward logic applies instead.
    const [omitWageGradeFromSubmit, setOmitWageGradeFromSubmit] = useState(false);
    // T3b (N1): display-only choice -- the Select never shows the UNSPECIFIED sentinel, only
    // 'MONTHLY' or 'PIECE_RATE'. `wageModeTouched` tracks whether the operator has interacted
    // with the Select at all this time the dialog is open; only a touched value is ever included
    // in the submit payload (see handleSubmit) -- an untouched Select omits wageMode entirely,
    // letting the backend's own carry-forward/no-change rule apply.
    const [wageMode, setWageMode] = useState<'MONTHLY' | 'PIECE_RATE'>('MONTHLY');
    const [wageModeTouched, setWageModeTouched] = useState(false);
    const { showApiError } = useToast();

    useEffect(() => {
        const fetchSegments = async () => {
            try {
                const res = await SegmentService.getByInstitution();
                const all: Segment[] = res.content ?? [];
                setSegments(all.filter(s => s.active !== false));
            } catch (err) {
                console.error('Error fetching segments:', err);
            }
        };
        fetchSegments();
        const fetchGrades = async () => {
            try {
                const data = await WageGradeService.getByInstitution();
                setWageGrades(data.filter(g => g.isActive));
            } catch (err) {
                console.error('Error fetching wage grades:', err);
            }
        };
        fetchGrades();
    }, []);

    useEffect(() => {
        if (open) {
            if (salary) {
                const preFilled: UserOption = { id: salary.userId, username: salary.userName, fullName: salary.employeeName };
                setSelectedUser(preFilled);
                setEffectiveFrom(salary.effectiveFrom);
                setDetails(salary.details.map(d => ({
                    salaryComponentId: d.salaryComponentId,
                    amount: d.amount
                })));
                setSegmentId(salary.segmentId ?? undefined);
                setWageGradeId(salary.wageGradeId ?? CLEAR_WAGE_GRADE_SENTINEL);
                setOmitWageGradeFromSubmit(false);
                setWageMode(salary.wageMode === 'PIECE_RATE' ? 'PIECE_RATE' : 'MONTHLY');
                setWageModeTouched(false);
                setAssignedInactiveGrade(
                    salary.wageGradeId && salary.wageGradeActive === false
                        ? { id: salary.wageGradeId, displayName: salary.wageGradeDisplayName || salary.wageGradeId }
                        : null
                );
            } else {
                setSelectedUser(null);
                setEffectiveFrom(toLocalIsoDate());
                setDetails([]);
                setSegmentId(undefined);
                setWageGradeId(CLEAR_WAGE_GRADE_SENTINEL);
                setOmitWageGradeFromSubmit(false);
                setWageMode('MONTHLY');
                setWageModeTouched(false);
                setAssignedInactiveGrade(null);
            }
        }
    }, [open, salary]);

    // R8: create-mode carry-forward prefill -- when a NEW assignment's employee is picked, fetch
    // their current active salary's grade (if any) so the Select reflects what the backend would
    // carry forward anyway; the operator can still override or explicitly clear it.
    useEffect(() => {
        if (salary || !selectedUser) return; // only for a brand-new assignment
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        PayrollService.getEmployeeSalaryForUser(selectedUser.id, institutionId)
            .then(existing => {
                setWageGradeId(existing.wageGradeId ?? CLEAR_WAGE_GRADE_SENTINEL);
                setOmitWageGradeFromSubmit(false);
            })
            .catch((error: any) => {
                if (error?.response?.status === 404) {
                    // Genuinely no prior salary -- "No grade" is the correct, known answer.
                    setWageGradeId(CLEAR_WAGE_GRADE_SENTINEL);
                    setOmitWageGradeFromSubmit(false);
                } else {
                    // M-1: an ambiguous failure (500, 403, network drop) -- we don't know whether
                    // a prior grade exists, so don't claim "No grade" by posting the clear
                    // sentinel. Omit the field on submit and let the backend's R8 carry-forward
                    // decide, exactly as if the operator had never touched the Select.
                    setOmitWageGradeFromSubmit(true);
                }
            });
    }, [selectedUser, salary]);

    const addDetail = () => {
        setDetails([...details, { salaryComponentId: '', amount: 0 }]);
    };

    const removeDetail = (index: number) => {
        setDetails(details.filter((_, i) => i !== index));
    };

    const updateDetail = (index: number, field: string, value: any) => {
        const updated = [...details];
        (updated[index] as any)[field] = value;
        setDetails(updated);
    };

    const handleSubmit = async () => {
        if (!selectedUser) return;
        const institutionId = AuthService.getSelectedInstitution()!!;
        const data: IEmployeeSalaryCreate = {
            institutionId,
            userId: selectedUser.id,
            effectiveFrom,
            details: details.filter(d => d.salaryComponentId && d.amount > 0),
            segmentId: segmentId || undefined,
            // M-1: omitted (not sent as the clear sentinel) when the carry-forward prefill fetch
            // failed ambiguously -- see omitWageGradeFromSubmit above.
            ...(omitWageGradeFromSubmit ? {} : { wageGradeId }),
            // T3b/N1: omitted entirely unless the operator touched the Select this session --
            // deliberately selecting "Monthly" posts the UNSPECIFIED sentinel (never the display
            // string 'MONTHLY'), while an untouched Select sends no wageMode key at all.
            ...(wageModeTouched
                ? { wageMode: wageMode === 'MONTHLY' ? WAGE_MODE_UNSPECIFIED : wageMode }
                : {}),
        };

        try {
            if (salary) {
                await PayrollService.updateEmployeeSalary(salary.id, data);
            } else {
                await PayrollService.createEmployeeSalary(data);
            }
            onSaved();
        } catch (error) {
            showApiError(error, 'Failed to save employee salary');
        }
    };

    const saveWhy = !selectedUser ? 'Choose an employee first.'
        : !effectiveFrom ? 'Set the Effective From date first.'
        : details.length === 0 ? 'Add at least one salary component first.' : null;

    const activeComponents = components.filter(c => c.isActive);
    const wageGradeOptions: WageGradeOption[] = [
        { id: CLEAR_WAGE_GRADE_SENTINEL, label: 'No grade' },
        ...wageGrades.map(g => ({ id: g.id, label: `${g.code} — ${g.displayName}` })),
        ...(assignedInactiveGrade
            ? [{ id: assignedInactiveGrade.id, label: `${assignedInactiveGrade.displayName} (inactive)` }]
            : []),
    ];

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.salaryForm.page' } as any} maxWidth="md" fullWidth>
            <DialogTitle>{salary ? 'Edit' : 'Assign'} Employee Salary</DialogTitle>
            <DialogContent data-help-section="payroll.salaryForm.section.details">
                <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 1 }}>
                    <EmployeeAutocomplete<UserOption>
                        data-help="payroll.salaryForm.field.employee" label="Employee"
                        size="medium"
                        fullWidth
                        value={selectedUser}
                        onChange={setSelectedUser}
                        disabled={!!salary}
                        onError={(error) => showApiError(error, 'Failed to search users')}
                    />
                    <TextField
                        data-help="payroll.salaryForm.field.effectiveFrom" label="Effective From"
                        type="date"
                        value={effectiveFrom}
                        onChange={(e) => setEffectiveFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <SearchableSelect
                        data-help="payroll.salaryForm.field.segment" label="Default Segment (optional)"
                        placeholder="(default)"
                        options={segments}
                        value={segmentId ?? ''}
                        onChange={id => setSegmentId(id || undefined)}
                        getOptionLabel={s => `${s.code} — ${s.name}`}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    {/* "No grade" is a real, explicit choice (the clear sentinel), so it stays an
                        option; the (x) button also maps back to it. The currently-assigned grade is
                        kept even when deactivated, labeled, so this can never silently null it out. */}
                    <SearchableSelect<WageGradeOption>
                        data-help="payroll.salaryForm.field.grade" label="Wage Grade (optional)"
                        options={wageGradeOptions}
                        value={wageGradeId}
                        onChange={id => {
                            // An explicit operator choice always wins over the "fetch failed,
                            // omit the field" fallback -- they have now stated an intent.
                            setOmitWageGradeFromSubmit(false);
                            setWageGradeId(id || CLEAR_WAGE_GRADE_SENTINEL);
                        }}
                        getOptionLabel={o => o.label}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="wage-mode-select-label" shrink>Wage Mode</InputLabel>
                        <Select
                            data-help="payroll.salaryForm.field.wageMode" labelId="wage-mode-select-label"
                            value={wageMode}
                            displayEmpty
                            label="Wage Mode"
                            onChange={(e) => {
                                setWageModeTouched(true);
                                setWageMode(e.target.value as 'MONTHLY' | 'PIECE_RATE');
                            }}
                        >
                            <MenuItem value="MONTHLY">Monthly</MenuItem>
                            <MenuItem value="PIECE_RATE">Piece Rate</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Overtime pay and piece-rate cannot both be active for one employee (R7).
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography data-help-section="payroll.salaryForm.section.components" variant="subtitle1">Salary Components</Typography>
                    <Button data-help="payroll.salaryForm.addComponent" startIcon={<AddIcon />} onClick={addDetail} size="small">
                        Add Component
                    </Button>
                </Box>

                {details.map((detail, index) => {
                    const comp = activeComponents.find(c => c.id === detail.salaryComponentId);
                    return (
                        <Box key={index} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                            <SearchableSelect
                                data-help="payroll.salaryForm.field.component" label="Component"
                                options={activeComponents}
                                value={detail.salaryComponentId}
                                onChange={id => updateDetail(index, 'salaryComponentId', id)}
                                getOptionLabel={c => c.name}
                                renderOption={(props, c) => (
                                    <li {...props} key={c.id}>
                                        {c.name}
                                        <Chip label={c.type} size="small" sx={{ ml: 1 }}
                                            color={c.type === 'EARNING' ? 'success' : 'error'} />
                                    </li>
                                )}
                                clearable={false}
                                sx={{ minWidth: 250 }}
                            />
                            <TextField
                                data-help="payroll.salaryForm.field.amount" label="Amount"
                                type="number"
                                value={detail.amount}
                                onChange={(e) => updateDetail(index, 'amount', parseFloat(e.target.value) || 0)}
                                size="small"
                                sx={{ width: 150 }}
                            />
                            {comp && (
                                <Chip label={comp.type} size="small"
                                    color={comp.type === 'EARNING' ? 'success' : 'error'} />
                            )}
                            <IconButton onClick={() => removeDetail(index)} size="small" color="error" data-help="payroll.salaryForm.removeComponent" aria-label="remove component">
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    );
                })}
                {details.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No components added. Click "Add Component" to start.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.salaryForm.cancel" onClick={onClose}>Cancel</Button>
                <Button data-help="payroll.salaryForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} onClick={handleSubmit} variant="contained"
                    disabled={!selectedUser || !effectiveFrom || details.length === 0}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeSalaryForm;
